import type { ComparisonValue, FilterExpression } from "../expressions/base/types.ts";
import { ComparisonExpression } from "../expressions/ComparisonExpression.ts";
import { FunctionExpression } from "../expressions/FunctionExpression.ts";
import { GroupExpression } from "../expressions/GroupExpression.ts";
import { InExpression } from "../expressions/InExpression.ts";
import { LambdaExpression } from "../expressions/LambdaExpression.ts";
import { LiteralExpression } from "../expressions/LiteralExpression.ts";
import { LogicalExpression } from "../expressions/LogicalExpression.ts";
import { NotExpression } from "../expressions/NotExpression.ts";
import { PropertyExpression } from "../expressions/PropertyExpression.ts";
import { type Token, TokenType } from "../odata-types.ts";
import { Lexer } from "./lexer.ts";

type FunctionTokenType = typeof TokenType.CONTAINS | typeof TokenType.STARTSWITH | typeof TokenType.ENDSWITH;

type ComparisonTokenType =
    | typeof TokenType.EQUALS
    | typeof TokenType.NOT_EQUALS
    | typeof TokenType.GREATER_THAN
    | typeof TokenType.GREATER_THAN_OR_EQUALS
    | typeof TokenType.LESS_THAN
    | typeof TokenType.LESS_THAN_OR_EQUALS;

class Parser {
    private tokens: Token[];
    private position: number = 0;
    private currentToken: Token;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
        this.currentToken = tokens[0];
    }

    private isTokenType(type: TokenType): boolean {
        return this.currentToken.type === type;
    }

    private isAnyTokenType(...types: TokenType[]): boolean {
        return types.includes(this.currentToken.type);
    }

    private advance(): void {
        this.position++;
        if (this.position < this.tokens.length) {
            this.currentToken = this.tokens[this.position];
        } else {
            this.currentToken = { type: TokenType.EOF, value: "", position: -1 };
        }
    }

    private consume(tokenType: TokenType): Token {
        if (this.currentToken.type === tokenType) {
            const token = this.currentToken;
            this.advance();

            return token;
        } else {
            throw new Error(
                `Expected ${tokenType} but got ${this.currentToken.type} at position ${this.currentToken.position}`
            );
        }
    }

    private expectOperand(operatorName: string): void {
        if (this.isAnyTokenType(TokenType.EOF, TokenType.AND, TokenType.OR, TokenType.RIGHT_PAREN)) {
            throw new Error(`Expected right operand for ${operatorName} at position ${this.currentToken.position}`);
        }
    }

    parse(): FilterExpression {
        let parenCount = 0;

        for (const token of this.tokens) {
            if (token.type === TokenType.LEFT_PAREN) {
                parenCount++;
            } else if (token.type === TokenType.RIGHT_PAREN) {
                parenCount--;
            }

            if (parenCount < 0) {
                throw new Error(`Unbalanced parentheses: unexpected closing parenthesis at position ${token.position}`);
            }
        }

        if (parenCount > 0) {
            throw new Error("Unbalanced parentheses: missing closing parenthesis");
        }

        const expression = this.parseLogicalExpression();

        if (this.currentToken.type !== TokenType.EOF) {
            throw new Error(`Unexpected token ${this.currentToken.type} at position ${this.currentToken.position}`);
        }

        return expression;
    }

    private parseLogicalExpression(): FilterExpression {
        let left = this.parseAndExpression();

        while (this.isTokenType(TokenType.OR)) {
            this.advance();
            this.expectOperand("logical operator or");

            const right = this.parseAndExpression();
            left = new LogicalExpression(TokenType.OR, left, right);
        }

        return left;
    }

    private parseAndExpression(): FilterExpression {
        let left = this.parseNotOrComparisonExpression();

        while (this.isTokenType(TokenType.AND)) {
            this.advance();
            this.expectOperand("logical operator and");

            const right = this.parseNotOrComparisonExpression();
            left = new LogicalExpression(TokenType.AND, left, right);
        }

        return left;
    }

    private parseNotOrComparisonExpression(): FilterExpression {
        if (this.currentToken.type === TokenType.NOT) {
            this.advance();
            this.expectOperand("'not' operator");
            const inner = this.parseComparisonExpression();
            return new NotExpression(inner);
        }
        return this.parseComparisonExpression();
    }

    private parseComparisonExpression(): FilterExpression {
        const left = this.parsePrimaryExpression();

        if (this.isTokenType(TokenType.IN)) {
            this.advance();
            this.consume(TokenType.LEFT_PAREN);

            const values: (string | number)[] = [];

            if (this.currentToken.type !== TokenType.RIGHT_PAREN) {
                values.push(this.parseLiteralValueForIn());

                while (this.isTokenType(TokenType.COMMA)) {
                    this.advance();

                    if (this.isTokenType(TokenType.RIGHT_PAREN)) {
                        throw new Error(`Expected value after comma at position ${this.currentToken.position}`);
                    }

                    values.push(this.parseLiteralValueForIn());
                }
            }

            this.consume(TokenType.RIGHT_PAREN);

            if (left.type !== "property") {
                throw new Error(
                    `Expected property expression before 'in' operator at position ${this.currentToken.position}`
                );
            }

            return new InExpression(left, values);
        }

        if (this.isComparisonToken(this.currentToken.type)) {
            const operator = this.currentToken.type;
            this.advance();
            this.expectOperand(`operator ${operator}`);

            const right = this.parseLiteralOrPropertyOrDateArithmetic();

            if (left.type !== "property") {
                throw new Error(
                    `Expected property expression on left side of comparison at position ${this.currentToken.position}`
                );
            }

            return new ComparisonExpression(operator, left, right);
        }

        return left;
    }

    private parseLiteralValueForIn(): string | number {
        const valueExpr = this.parseLiteralOrProperty();

        if (valueExpr.type !== "literal") {
            throw new Error(`Expected literal value in 'in' expression at position ${this.currentToken.position}`);
        }

        if (typeof valueExpr.value !== "string" && typeof valueExpr.value !== "number") {
            throw new Error(`Expected string or number in 'in' expression at position ${this.currentToken.position}`);
        }

        return valueExpr.value;
    }

    private static readonly FILTER_TYPES = new Set(["comparison", "function", "in", "lambda", "logical", "group"]);

    private parsePrimaryExpression(): FilterExpression {
        if (this.currentToken.type === TokenType.LEFT_PAREN) {
            this.advance();
            const inner = this.parseLogicalExpression();
            this.consume(TokenType.RIGHT_PAREN);

            if (Parser.FILTER_TYPES.has(inner.type)) {
                const operator = inner.type === "logical" || inner.type === "group" ? inner.operator : TokenType.AND;
                return new GroupExpression(operator, inner);
            }

            return inner;
        } else if (this.isFunctionToken(this.currentToken.type)) {
            return this.parseFunctionCall();
        } else if (this.currentToken.type === TokenType.IDENTIFIER) {
            return this.parsePropertyOrLambda();
        } else {
            throw new Error(`Unexpected token ${this.currentToken.type} at position ${this.currentToken.position}`);
        }
    }

    // Appends subsequent /identifier segments to the array.
    // Returns true if it stopped because ANY/ALL was found (caller should call parseLambdaExpression).
    private parseTrailingSegments(segments: string[]): boolean {
        while (this.isTokenType(TokenType.SLASH)) {
            this.advance();
            if (this.isAnyTokenType(TokenType.ANY, TokenType.ALL)) {
                return true;
            }
            if (this.isTokenType(TokenType.IDENTIFIER)) {
                segments.push(this.currentToken.value);
                this.advance();
            } else {
                throw new Error(`Expected property name after '/' at position ${this.currentToken.position}`);
            }
        }
        return false;
    }

    private parsePropertyOrLambda(): FilterExpression {
        const token = this.currentToken;
        this.advance();
        const segments: string[] = [token.value];

        if (!this.isTokenType(TokenType.SLASH)) {
            return new PropertyExpression(segments);
        }

        this.advance();

        if (this.isAnyTokenType(TokenType.ANY, TokenType.ALL)) {
            return this.parseLambdaExpression(segments);
        }

        if (!this.isTokenType(TokenType.IDENTIFIER)) {
            throw new Error(`Expected property name after '/' at position ${this.currentToken.position}`);
        }
        segments.push(this.currentToken.value);
        this.advance();

        if (this.parseTrailingSegments(segments)) {
            return this.parseLambdaExpression(segments);
        }

        return new PropertyExpression(segments);
    }

    private parseLambdaExpression(collection: string[]): LambdaExpression {
        const operatorToken = this.currentToken;

        if (operatorToken.type !== TokenType.ANY && operatorToken.type !== TokenType.ALL) {
            throw new Error(`Expected 'any' or 'all' operator at position ${operatorToken.position}`);
        }

        const operator = operatorToken.type;
        this.advance();

        this.consume(TokenType.LEFT_PAREN);

        if (this.isTokenType(TokenType.RIGHT_PAREN)) {
            if (operator === TokenType.ALL) {
                throw new Error(`'all' operator requires parameters at position ${this.currentToken.position}`);
            }

            this.advance();

            return new LambdaExpression(operator, collection);
        }

        if (!this.isTokenType(TokenType.IDENTIFIER)) {
            throw new Error(`Expected lambda variable at position ${this.currentToken.position}`);
        }
        const variable = this.currentToken.value;
        this.advance();

        this.consume(TokenType.COLON);

        const predicate = this.parseLogicalExpression();

        this.consume(TokenType.RIGHT_PAREN);

        return new LambdaExpression(operator, collection, variable, predicate);
    }

    private parseLiteralOrProperty(): FilterExpression {
        if (this.isTokenType(TokenType.IDENTIFIER)) {
            const token = this.currentToken;
            this.advance();

            if (this.isTokenType(TokenType.SLASH)) {
                this.advance();

                if (this.isTokenType(TokenType.IDENTIFIER)) {
                    const segments: string[] = [token.value, this.currentToken.value];
                    this.advance();
                    if (this.parseTrailingSegments(segments)) {
                        throw new Error(
                            `Nested lambda expressions are not supported at position ${this.currentToken.position}`
                        );
                    }
                    return new PropertyExpression(segments);
                }
            }

            return new PropertyExpression([token.value]);
        } else if (this.isTokenType(TokenType.STRING)) {
            const token = this.currentToken;
            this.advance();

            return new LiteralExpression(token.value);
        } else if (this.isTokenType(TokenType.NUMBER)) {
            const token = this.currentToken;
            this.advance();

            return new LiteralExpression(parseFloat(token.value));
        } else if (this.isTokenType(TokenType.BOOLEAN)) {
            const token = this.currentToken;
            this.advance();

            return new LiteralExpression(token.value === "true");
        } else if (this.isTokenType(TokenType.NULL)) {
            this.advance();

            return new LiteralExpression(null);
        } else {
            throw new Error(
                `Expected literal or property but got ${this.currentToken.type} at position ${this.currentToken.position}`
            );
        }
    }

    private parseNowExpression(): ComparisonValue {
        this.consume(TokenType.LEFT_PAREN);
        this.consume(TokenType.RIGHT_PAREN);

        if (!this.isAnyTokenType(TokenType.SUB, TokenType.ADD)) {
            return { type: "now" };
        }

        const operator = this.currentToken.type === TokenType.SUB ? TokenType.SUB : TokenType.ADD;
        this.advance();

        if (this.currentToken.type !== TokenType.DURATION) {
            throw new Error(`Expected 'duration' at position ${this.currentToken.position}`);
        }
        this.advance();

        const durationToken = this.currentToken;
        if (durationToken.type !== TokenType.STRING) {
            throw new Error(`Expected duration string literal at position ${durationToken.position}`);
        }
        this.advance();

        return {
            type: "relativeDate",
            operator,
            duration: durationToken.value
        };
    }

    private parseLiteralOrPropertyOrDateArithmetic(): ComparisonValue {
        if (this.isTokenType(TokenType.NOW)) {
            this.advance();
            return this.parseNowExpression();
        }

        const expr = this.parseLiteralOrProperty();

        if (expr.type === "literal") {
            return { type: "literal", value: expr.value };
        }

        throw new Error(`Expected literal value but got ${expr.type} at position ${this.currentToken.position}`);
    }

    private parseFunctionCall(): FilterExpression {
        const functionToken = this.currentToken;

        if (!this.isFunctionToken(functionToken.type)) {
            throw new Error(`Expected string function at position ${functionToken.position}`);
        }

        const functionName = functionToken.type;
        this.advance();

        this.consume(TokenType.LEFT_PAREN);

        const parameters: FilterExpression[] = [];

        if (this.currentToken.type !== TokenType.RIGHT_PAREN) {
            parameters.push(this.parseLiteralOrProperty());

            while (this.isTokenType(TokenType.COMMA)) {
                this.advance();

                if (this.isTokenType(TokenType.RIGHT_PAREN)) {
                    throw new Error(`Expected parameter after comma at position ${this.currentToken.position}`);
                }

                parameters.push(this.parseLiteralOrProperty());
            }
        }

        this.consume(TokenType.RIGHT_PAREN);

        if (parameters.length !== 2) {
            throw new Error(`Function ${functionName} requires exactly 2 parameters`);
        }

        const firstParam = parameters[0];
        const secondParam = parameters[1];

        if (firstParam.type !== "property") {
            throw new Error(`First parameter of ${functionName} must be a property`);
        }

        if (secondParam.type !== "literal") {
            throw new Error(`Second parameter of ${functionName} must be a literal`);
        }

        if (typeof secondParam.value !== "string") {
            throw new Error(`Second parameter of ${functionName} must be a string`);
        }

        return new FunctionExpression(functionName, firstParam, secondParam.value);
    }

    private isFunctionToken(tokenType: TokenType): tokenType is FunctionTokenType {
        return (
            tokenType === TokenType.CONTAINS || tokenType === TokenType.STARTSWITH || tokenType === TokenType.ENDSWITH
        );
    }

    private isComparisonToken(tokenType: TokenType): tokenType is ComparisonTokenType {
        return (
            tokenType === TokenType.EQUALS ||
            tokenType === TokenType.NOT_EQUALS ||
            tokenType === TokenType.GREATER_THAN ||
            tokenType === TokenType.GREATER_THAN_OR_EQUALS ||
            tokenType === TokenType.LESS_THAN ||
            tokenType === TokenType.LESS_THAN_OR_EQUALS
        );
    }
}

export function parseODataFilter(filterString?: string) {
    if (!filterString || filterString.trim() === "") {
        return;
    }

    try {
        const lexer = new Lexer(filterString);
        const tokens = lexer.tokenize();
        const parser = new Parser(tokens);

        return parser.parse();
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`Failed to parse filter: ${error.message}`, { cause: error });
        }
        throw error;
    }
}
